﻿import { useTranslation } from 'react-i18next'

import { useTheme } from '../theme/ThemeProvider'
import { CheckIcon } from './icons'
import { SegmentedControl, type SegmentedOption } from './SegmentedControl'
import type { ThemePreference } from '../theme/theme'

type ThemeToggleProps = {
  variant?: 'cards' | 'compact'
  showLabel?: boolean
}

export function ThemeToggle({ variant = 'cards', showLabel = false }: ThemeToggleProps) {
  const { t } = useTranslation()
  const { preference, setTheme } = useTheme()
  const options: SegmentedOption<ThemePreference>[] = [
    { value: 'light', label: t('theme.light') },
    { value: 'dark', label: t('theme.dark') },
    { value: 'system', label: t('theme.system') },
  ]

  if (variant === 'compact' && !showLabel) {
    return <SegmentedControl options={options} value={preference} onChange={setTheme} aria-label={t('theme.label')} />
  }

  if (variant === 'compact') {
    return (
      <section className="theme-switcher" aria-label={t('theme.label')}>
        <div className="theme-switcher-heading">
          <ThemeIcon kind="system" />
          <span>{t('theme.label')}</span>
        </div>
        <div className="theme-switcher-options" role="group" aria-label={t('theme.label')}>
          {options.map((option) => {
            const selected = preference === option.value
            return (
              <button
                key={option.value}
                type="button"
                className={selected ? 'theme-switcher-option theme-switcher-option-active' : 'theme-switcher-option'}
                aria-pressed={selected}
                onClick={() => setTheme(option.value)}
                title={option.label}
              >
                <ThemeIcon kind={option.value} />
                <span>{option.label}</span>
              </button>
            )
          })}
        </div>
      </section>
    )
  }

  return (
    <div className="theme-options" role="radiogroup" aria-label={t('theme.label')}>
      {options.map((option) => {
        const selected = preference === option.value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            className={selected ? 'theme-option theme-option-selected' : 'theme-option'}
            onClick={() => setTheme(option.value)}
          >
            {selected && (
              <span className="theme-option-badge" aria-hidden="true">
                <CheckIcon size={12} />
              </span>
            )}
            <span className={`theme-option-preview theme-option-preview-${option.value}`} aria-hidden="true">
              <span className="theme-option-preview-bar">
                <span />
                <span />
                <span />
              </span>
              <span className="theme-option-preview-line theme-option-preview-line-a" />
              <span className="theme-option-preview-line theme-option-preview-line-b" />
              <span className="theme-option-preview-chip" />
            </span>
            <span className="theme-option-copy">
              <span className="theme-option-title">
                <ThemeIcon kind={option.value} />
                {option.label}
              </span>
              <span className="theme-option-description">{t('theme.' + option.value + 'Description')}</span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

function ThemeIcon({ kind }: { kind: ThemePreference }) {
  if (kind === 'light') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
  }
  if (kind === 'dark') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 14.3A8.3 8.3 0 0 1 9.7 4 8.3 8.3 0 1 0 20 14.3Z" /></svg>
  }
  return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="4" width="17" height="12" rx="2" /><path d="M8.5 20h7M12 16v4" /></svg>
}
